import { useGoalsForUser } from "@coaching-culture/api-client";
import { GoalDto } from "@coaching-culture/types";
import { useUser } from "auth";
import { useGoalTerminology } from "auth/OrgProvider";
import { fromUnixTime } from "date-fns";
import { sortBy } from "lodash";
import {
  Box,
  Button,
  EmptyState,
  Flex,
  formatDate,
  Label,
  ProgressBar,
  Skeleton,
} from "@coaching-culture/ui";
import { FaBullseye } from "react-icons/fa";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { CardBody, HomeCard, LoadError, Muted, RuleList } from "./styles";

/** A goal's name: dark text, like the to-do rows, not link blue. */
const GoalLink = styled(Link)`
  font-weight: 600;
  color: ${(p) => p.theme.colors.black};
  overflow-wrap: anywhere;

  &:hover {
    color: ${(p) => p.theme.colors.black};
    text-decoration: underline;
  }
`;

/** How many goals the card lists. */
const GOALS_SHOWN = 3;

const percentOf = (g: GoalDto) =>
  g.progress == null || g.target == null || g.target <= 0
    ? null
    : Math.max(0, Math.min(100, Math.round((g.progress / g.target) * 100)));

export function MyGoalsCard() {
  const [user] = useUser();
  const terms = useGoalTerminology();
  const goals = useGoalsForUser(user.id);
  const title = `My ${terms.goal.asPluralBody()}`;

  const open = sortBy(
    (goals.data ?? []).filter((g) => g.completedOn == null),
    [(g) => !g.isStarred, (g) => g.deadline],
  );

  return (
    <HomeCard
      title={title}
      titleId="home-goals-title"
      busy={goals.isLoading}
      action={<Link to="/solutions/performance/goals">View all</Link>}
    >
      {goals.isLoading ? (
        <CardBody>
          {[0, 1, 2].map((i) => (
            <Box key={i} mb={3}>
              <Skeleton width="70%" mb={2} />
              <Skeleton height={6} />
            </Box>
          ))}
        </CardBody>
      ) : goals.isError ? (
        <LoadError
          message={`We couldn't load your ${terms.goal.asPluralBody()}.`}
          onRetry={() => goals.refetch()}
        />
      ) : open.length === 0 ? (
        <EmptyState
          compact
          icon={FaBullseye}
          action={
            <Button
              small
              filled={false}
              to="/solutions/performance/goals/create"
            >
              Create a {terms.goal.asBody()}
            </Button>
          }
        >
          You have no open {terms.goal.asPluralBody()}.
        </EmptyState>
      ) : (
        <CardBody>
          <RuleList>
            {open.slice(0, GOALS_SHOWN).map((g) => {
              const pct = percentOf(g);
              const late = fromUnixTime(g.deadline) < new Date();
              return (
                <Box as="li" key={g.id} py={2}>
                  <Flex
                    alignItems="baseline"
                    justifyContent="space-between"
                    mb={1}
                    style={{ gap: 10 }}
                  >
                    <span style={{ minWidth: 0 }}>
                      <GoalLink
                        to={`/solutions/performance/goals/${g.id}/review`}
                      >
                        {g.purpose}
                      </GoalLink>
                      {g.status === "blocked" && (
                        <Label size="tiny" color="danger" ml={1}>
                          Blocked
                        </Label>
                      )}
                      {late && g.status !== "blocked" && (
                        <Label size="tiny" color="danger" ml={1}>
                          Past deadline
                        </Label>
                      )}
                    </span>
                    <Muted as="span" style={{ whiteSpace: "nowrap" }}>
                      {pct != null
                        ? `${pct}%`
                        : `Due ${formatDate(g.deadline)}`}
                    </Muted>
                  </Flex>
                  {pct != null && (
                    <ProgressBar
                      value={pct / 100}
                      label={`${g.purpose} progress`}
                    />
                  )}
                </Box>
              );
            })}
          </RuleList>
          {open.length > GOALS_SHOWN && (
            <Muted mt={2} mb={0}>
              and {open.length - GOALS_SHOWN} more
            </Muted>
          )}
        </CardBody>
      )}
    </HomeCard>
  );
}
